// Batch checklist entry — spreadsheet-style table posting to
// POST /projects/:projectId/checklist/bulk. One row per item:
// Item • Description • Activity • Due • Responsible • Require screenshot.
//
// The same two things the single add form on /checklists cannot do: say
// WHAT the item is (Description is the item's notes — what to check and
// what "done" looks like), and hand it to somebody without opening the
// row afterwards (Responsible is picked from the project roster, grouped
// by company: the owner company's own people, every partner company's
// members, and partner contacts without an account, who are given a
// magic-login account on save exactly as the checklist page does).
//
// CSV paste (papaparse) appends rows: WITH a header row columns are
// matched by name, so Description can sit anywhere and a sheet without
// one still imports; without a header the fixed order above applies.
// A pasted responsible is matched against the roster by email or name —
// unmatched text is left in the cell rather than guessed at.
import React, { useEffect, useMemo, useState } from "react";
import { filterPeopleOption, renderPeopleOption } from "components/PeoplePicker/PeoplePicker";
import { peoplePickerOptions, userOrEmailRef } from "components/PeoplePicker/peoplePickerOptions";
import { useHistory } from "react-router-dom";
import {
  Alert, Button, Checkbox, Empty, Input, Popconfirm, Select, Table, Tag, Tooltip, message,
} from "antd";
import { DeleteOutlined, PlusOutlined, SendOutlined } from "@ant-design/icons";
import Papa from "papaparse";
import { url, tokenRef } from "api";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import useLastProject from "components/Coordination/useLastProject";
import useProjectAccess from "hooks/useProjectAccess";
import LocationPicker from "components/Coordination/LocationPicker";
import ActivityPicker from "components/Coordination/ActivityPicker";
import { withActivityPaths } from "helpers/schedule/activityLabel";
import { matchOption } from "components/SearchSelect/searchSelect";

function auth() {
  return { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` };
}
const api = async (path, options = {}) => {
  const res = await fetch(`${url}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...auth(), ...(options.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
  return body;
};

const emptyRow = () => ({
  key: Math.random().toString(36).slice(2),
  title: "", notes: "", ganttItemId: "", dueDate: "",
  responsibleUserId: "", responsibleCompanyId: "", responsibleText: "",
});

export default function BatchChecklistEntryPage() {
  const history = useHistory();
  const [projects, setProjects] = useState([]);
  // Shared coordination memory: ?projectId= wins, else the last project
  // picked anywhere in the suite, else the first project (below).
  const [projectId, setProjectId] = useLastProject(projects);
  const [planItems, setPlanItems] = useState([]);
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
  const [requireShot, setRequireShot] = useState(false);
  const [notifyPeople, setNotifyPeople] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const { can } = useProjectAccess(projectId);

  useEffect(() => {
    fetch(`${url}/projects/picker`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : []))
      .then((ps) => {
        const list = Array.isArray(ps) ? ps : (ps?.projects || []);
        setProjects(list);
        if ((!projectId || !list.some((p) => p._id === projectId)) && list[0]?._id) setProjectId(list[0]._id);
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!projectId) { setPlanItems([]); return; }
    fetch(`${url}/projects/${projectId}/gantt`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((feed) => setPlanItems(withActivityPaths(feed.items || []).filter((it) => it.status !== "done")))
      .catch(() => setPlanItems([]));
  }, [projectId]);

  useEffect(() => {
    if (!projectId) { setRoster([]); return; }
    let alive = true;
    setRosterLoading(true);
    api(`/projects/${projectId}/participants/roster`)
      .then((r) => { if (alive) setRoster(Array.isArray(r) ? r : []); })
      .catch(() => { if (alive) setRoster([]); })
      .finally(() => { if (alive) setRosterLoading(false); });
    return () => { alive = false; };
  }, [projectId]);

  const setCell = (key, patch) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  // Grouped by company so it is obvious who works for whom. The VALUE is
  // what the checklist route already understands: a user id, or
  // "email:<address>" for a roster contact with no account yet (one is
  // created for them on save, nothing is emailed).
  const responsibleGroups = useMemo(
    // The shared picker's options (components/PeoplePicker, owner
    // 2026-10-15): "company - trade" headers, people tagged by kind, a
    // partner executive greyed out (a reader is never responsible).
    () => peoplePickerOptions(roster, { personValue: userOrEmailRef, executives: "disable" })
      .map((g) => ({
        ...g,
        options: g.options.map((o) => ({
          ...o,
          companyId: o.group.companyId,
          email: String((o.person && o.person.email) || "").toLowerCase(),
          name: (o.person && o.person.name) || "",
        })),
      })),
    [roster]
  );
  const allPeople = useMemo(
    () => responsibleGroups.reduce((acc, g) => acc.concat(g.options.filter((o) => !o.disabled)), []),
    [responsibleGroups]
  );


  const onPaste = (text) => {
    const parsed = Papa.parse(String(text || "").trim(), { skipEmptyLines: true });
    const data = parsed?.data || [];
    if (!data.length) return;
    const header = data[0].map((c) => String(c).toLowerCase().trim());
    const hasHeader = header.some((h) => h === "item" || h === "title");
    const body = hasHeader ? data.slice(1) : data;
    const LEGACY = ["item", "description", "activity", "due", "responsible"];
    const idxOf = (names) => {
      if (!hasHeader) return LEGACY.findIndex((n) => names.includes(n));
      for (const n of names) {
        const i = header.findIndex((h) => h === n || h.startsWith(n));
        if (i >= 0) return i;
      }
      return -1;
    };
    const col = {
      title: idxOf(["item", "title", "check"]),
      notes: idxOf(["description", "notes", "note", "comment", "details"]),
      activity: idxOf(["activity", "plan item", "schedule"]),
      due: idxOf(["due", "due date"]),
      responsible: idxOf(["responsible", "assignee", "owner", "email"]),
    };
    const at = (cols, i) => (i >= 0 ? String(cols[i] || "").trim() : "");
    const added = body
      .filter((cols) => at(cols, col.title))
      .map((cols) => {
        const who = at(cols, col.responsible).toLowerCase();
        // Matched against the roster by address or name; anything else is
        // kept as text in the cell for the user to pick properly.
        const hit = who
          ? allPeople.find((o) => o.email === who || o.name.toLowerCase() === who)
          : null;
        return {
          ...emptyRow(),
          title: at(cols, col.title),
          notes: at(cols, col.notes),
          ganttItemId:
            planItems.find(
              (it) => it.name?.toLowerCase() === at(cols, col.activity).toLowerCase()
            )?._id || "",
          dueDate: at(cols, col.due),
          responsibleUserId: hit ? hit.value : "",
          responsibleCompanyId: hit ? hit.companyId : "",
          responsibleText: hit ? "" : at(cols, col.responsible),
        };
      });
    if (added.length) {
      setRows((rs) => [...rs.filter((r) => r.title.trim()), ...added]);
      message.success(`${added.length} row(s) pasted`);
    }
  };

  const submit = async () => {
    const payloadRows = rows
      .filter((r) => r.title.trim())
      .map((r) => ({
        title: r.title,
        // The item's notes: what to check and what done looks like.
        notes: r.notes || undefined,
        ganttItemId: r.ganttItemId || undefined,
        dueDate: r.dueDate || undefined,
        responsibleUserId: r.responsibleUserId || undefined,
        responsibleCompanyId: r.responsibleCompanyId || undefined,
        location: r.location && (r.location.level || r.location.area || r.location.grid) ? r.location : undefined,
      }));
    if (!payloadRows.length) return message.warning("Nothing to create — every row needs an item");
    setSaving(true);
    setResult(null);
    try {
      const res = await api(`/projects/${projectId}/checklist/bulk`, {
        method: "POST",
        body: JSON.stringify({ rows: payloadRows, requireScreenshot: requireShot, notifyResponsible: notifyPeople }),
      });
      setResult(res);
      if (res.created?.length) {
        message.success(`${res.created.length} item(s) created${res.notified ? `, ${res.notified} person(s) emailed` : ""}`);
        // Keep only the rows that failed, so a fix-and-resend is possible.
        const bad = new Set((res.errors || []).map((e) => e.row));
        const kept = rows.filter((r) => r.title.trim());
        setRows(kept.filter((r, i) => bad.has(i)).concat(bad.size ? [] : [emptyRow(), emptyRow(), emptyRow()]));
      }
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: "Item", dataIndex: "title",
      render: (v, r) => (
        <Input size="small" value={v} placeholder="What has to be checked"
               onChange={(e) => setCell(r.key, { title: e.target.value })} />
      ),
    },
    {
      title: "Description", dataIndex: "notes", width: 280,
      render: (v, r) => (
        <Input.TextArea size="small" value={v} autoSize={{ minRows: 1, maxRows: 4 }}
                        placeholder="What to check, and what done looks like"
                        onChange={(e) => setCell(r.key, { notes: e.target.value })} />
      ),
    },
    {
      // The same levels / areas / grid an issue is located by.
      title: "Location (level · area · grid)", key: "location", width: 350,
      render: (_, r) => (
        <LocationPicker projectId={projectId} size="small" compact value={r.location || {}}
                        onChange={(loc) => setCell(r.key, { location: loc })} />
      ),
    },
    {
      title: "Activity", dataIndex: "ganttItemId", width: 200,
      render: (v, r) => (
        <ActivityPicker size="small" items={planItems} style={{ width: "100%", minWidth: 0 }}
                value={v || undefined} placeholder="Activity or category"
                onChange={(val) => setCell(r.key, { ganttItemId: val || "" })} />
      ),
    },
    {
      title: "Due", dataIndex: "dueDate", width: 140,
      render: (v, r) => (
        <Tooltip title="Left empty, a linked activity lends its end date">
          <Input size="small" type="date" value={v}
                 onChange={(e) => setCell(r.key, { dueDate: e.target.value })} />
        </Tooltip>
      ),
    },
    {
      title: "Responsible", dataIndex: "responsibleUserId", width: 230,
      render: (v, r) => (
        <Select
          size="small" allowClear showSearch style={{ width: "100%" }}
          value={v || undefined}
          placeholder={rosterLoading ? "loading people…" : (r.responsibleText || "person on this project")}
          status={r.responsibleText && !v ? "warning" : undefined}
          filterOption={filterPeopleOption}
          optionRender={renderPeopleOption}
          notFoundContent={rosterLoading ? "loading…" : "Nobody on this project matches"}
          options={responsibleGroups}
          onChange={(val, opt) => setCell(r.key, {
            responsibleUserId: val || "",
            responsibleCompanyId: val ? (opt?.companyId || "") : "",
            responsibleText: "",
          })}
        />
      ),
    },
    {
      title: "", key: "del", width: 40,
      render: (_, r) => (
        <Button size="small" type="text" icon={<DeleteOutlined />}
                onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))} />
      ),
    },
  ];

  const filled = rows.filter((r) => r.title.trim()).length;

  return (
    <div style={{ padding: 24, fontFamily: "Inter, system-ui, sans-serif", maxWidth: 1400, margin: "0 auto" }}
         onPaste={(e) => {
           // Only intercept multi-row pastes (contains a newline) so
           // normal single-field pasting still works.
           const text = e.clipboardData?.getData("text") || "";
           if (/\n/.test(text)) { e.preventDefault(); onPaste(text); }
         }}>
      <CoordinationBreadcrumbs items={[{ label: "Checklists", to: "/checklists" }]} current="Batch entry" />
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Add multiple checklist items</h2>
        <Select filterOption={matchOption} style={{ width: 260 }} value={projectId || undefined}
                placeholder="Project" showSearch optionFilterProp="label"
                options={projects.map((p) => ({ value: p._id, label: p.title || p.name }))}
                onChange={setProjectId} />
        <span style={{ flex: 1 }} />
        <Button icon={<PlusOutlined />} onClick={() => setRows((rs) => [...rs, emptyRow()])}>Add row</Button>
        <Popconfirm title={`Create ${filled} checklist item(s)?`} onConfirm={submit}>
          <Button type="primary" icon={<SendOutlined />} loading={saving}
                  disabled={!projectId || !filled || !can.checklistAdd}>
            Create items
          </Button>
        </Popconfirm>
        <Button onClick={() => history.push(projectId ? `/checklists?projectId=${projectId}` : "/checklists")}>
          Back to checklists
        </Button>
      </div>

      {projectId && !can.checklistAdd && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
               message="View only: adding checklist items needs the checklistAdd permission on the project-owner side." />
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap", fontSize: 12 }}>
        <Tooltip title="Nobody (partner, supervisor or owner) can mark these items complete until a screenshot of the work is attached.">
          <Checkbox checked={requireShot} onChange={(e) => setRequireShot(e.target.checked)}>
            <b>Require a screenshot to complete (all rows)</b>
          </Checkbox>
        </Tooltip>
        <Tooltip title="One email per person, listing every row they are responsible for - the way a new issue tells its assignees. Off by default here because a batch is often typed in before the people are settled; the daily digest covers them either way.">
          <Checkbox checked={notifyPeople} onChange={(e) => setNotifyPeople(e.target.checked)}>
            <b>Email the responsible people</b>
          </Checkbox>
        </Tooltip>
      </div>

      <Alert type="info" showIcon style={{ marginBottom: 12 }}
             message="Paste CSV rows anywhere on this page to append them (columns: Item, Description, Activity, Due, Responsible — header row optional). An item with an activity and no due date inherits the activity's end date." />

      <Table rowKey="key" dataSource={rows} columns={columns} size="small" pagination={false}
             locale={{ emptyText: <Empty description="No rows — add or paste" /> }} />

      {result && (
        <div style={{ marginTop: 16 }}>
          {result.created?.length > 0 && (
            <Alert
              type="success" showIcon style={{ marginBottom: 8 }}
              message={`${result.created.length} checklist item(s) created`}
              description="They are on the Checklists page under this project — filter by Created today to see just these."
              action={
                // Carry the project, or the list opens on whichever project
                // it happened to remember and they look missing.
                <Button size="small" type="primary"
                        onClick={() => history.push(`/checklists?projectId=${projectId}&createdToday=1`)}>
                  View these items
                </Button>
              }
            />
          )}
          {result.errors?.length > 0 && (
            <Alert type="warning" showIcon
                   message={`Rows with errors (kept in the table): ${result.errors.map((e) => `row ${e.row + 1}: ${e.err}`).join("; ")}`} />
          )}
        </div>
      )}

      {rows.length > 0 && (
        <Tag style={{ marginTop: 12 }} color="default">{filled} row(s) with an item</Tag>
      )}
    </div>
  );
}
